/**
 * CF-050 — Blog Related Articles: horizontal carousel, BlogSummary styling (grid cards, not featured).
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Flex, Heading, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {SWIPER_MODULE_DEFAULTS} from '@amblique/base/app/constants/swiper-options';
import {Component} from '@amblique/king-living/app/components/contentful/component';
import {BlogSummary} from '@amblique/king-living/app/components/contentful/blog-summary';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {ACCOUNT_LANDING_LAYOUT_CONTEXT} from '@amblique/king-living/app/constants/account';
import {useBlogPageSlugsByIds} from '@amblique/king-living/app/contentful/hooks/use-blog-page-slugs-by-ids';
import {resolveContentfulSectionHeading} from '@amblique/king-living/app/contentful/helpers/section-heading';

/**
 * @param {Object} props
 * @param {Object} props.component - BlogRelatedArticles entry from Contentful
 */
export const BlogRelatedArticles = ({
  component,
  headingFallback,
  isComplete = true,
  isFetching = false,
  layoutContext,
  ...props
}) => {
  const {blogTagsCollection, sys} = component;
  const styles = useMultiStyleConfig('contentful/blog-related-articles', {});
  const isAccountLanding = layoutContext === ACCOUNT_LANDING_LAYOUT_CONTEXT;

  const headingText = resolveContentfulSectionHeading(
    component,
    isAccountLanding ? headingFallback : undefined
  );

  const blogs = useMemo(
    () => (blogTagsCollection?.items || []).filter(Boolean),
    [blogTagsCollection]
  );

  const blogIdsNeedingPageSlug = useMemo(
    () =>
      blogs
        .filter((blog) => !String(blog?.url || '').trim() && blog?.sys?.id)
        .map((blog) => blog.sys.id),
    [blogs]
  );

  const {data: pageSlugByBlogId} = useBlogPageSlugsByIds(blogIdsNeedingPageSlug);

  const swiperScopeClass = useMemo(
    () => `swiper-br-${sys?.id || 'related'}`.replace(/[^a-zA-Z0-9_-]/g, ''),
    [sys?.id]
  );

  const swiperOptions = useMemo(
    () => ({
      navigation: {
        ...SWIPER_MODULE_DEFAULTS.navigation,
        prevEl: `.${swiperScopeClass} > .swiper-navigation-pagination .swiper-button-prev`,
        nextEl: `.${swiperScopeClass} > .swiper-navigation-pagination .swiper-button-next`,
      },
    }),
    [swiperScopeClass]
  );

  if (!blogs.length && !isFetching) {
    return null;
  }

  return (
    <Box {...styles.root} {...props}>
      {headingText ? (
        <Heading as="h2" {...styles.heading}>
          {headingText}
        </Heading>
      ) : null}

      <Box {...styles.carouselRegion}>
        {!isComplete && isFetching ? (
          <Skeleton height="360px" width="100%" />
        ) : (
          <Swiper
            className={swiperScopeClass}
            options={swiperOptions}
            presets={isAccountLanding ? 'account-landing-get-inspired' : 'CF-050'}
            variant="blog-related-articles"
          >
            {blogs.map((blog) => (
              <Box
                key={blog.sys?.id || blog.title}
                height="auto"
                maxW="100%"
                sx={{
                  '&.swiper-slide': {
                    height: 'auto',
                  },
                }}
                width="100%"
              >
                <Component
                  as={BlogSummary}
                  allowCoverSwipe
                  component={blog}
                  isComplete={isComplete}
                  isFetching={isFetching}
                  slug={
                    String(blog?.url || '').trim() ?
                      undefined :
                      pageSlugByBlogId?.get(blog?.sys?.id)
                  }
                />
              </Box>
            ))}
          </Swiper>
        )}
      </Box>
    </Box>
  );
};

BlogRelatedArticles.displayName = 'BlogRelatedArticles';
BlogRelatedArticles.propTypes = {
  component: PropTypes.object.isRequired,
  headingFallback: PropTypes.string,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  layoutContext: PropTypes.string,
};
